import React, { useEffect, useState, useContext } from 'react';
import { useQuery } from '@apollo/client';
import Filter from 'src/elements/Filter';
import ReactPaginate from "react-paginate";
import styled from 'styled-components';
import '../../styles/styles.css';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

import { Loading } from 'src/components/DataState';
import {
  CardsContainer,
} from './elements';
import Card from './Card';
import InventoryHeader from '../Headers/InventoryHeader';
import ResourcesContext from 'src/contexts/ResourcesContext';
import FinanceLink from '../FinanceLink';

import { ASSETS_WITH_COUNT, ASSET_NAV_LIST } from './queries';

const PAGE_SIZE = 24;
const HOME_PREVIEW_SIZE = 6;

const StyledReactPaginate = styled(ReactPaginate)`
  display: flex;
  flex-wrap: nowrap;
  font-size: smaller;
  color: black;
`;

const ButtonContainer = styled(Link)`
  background-color: #fff;
  width: fit-content;
  color: #000;
  font-size: 18px;
  font-weight: 400;
  padding: 20px 50px 20px 50px;
  border-radius: 10px;
  border: 1px solid black;
`;

const SmallButtonContainer = styled(ButtonContainer)`
  font-size: 13px;
  padding: 5px 14px;
  border-radius: 6px;
  white-space: nowrap;
`;

const Inventory = ({ buyNow, homeIndex, saleId, watching }) => {
  const {
    setCurrentAssets,
    inventoryFilterValues,
    setInventoryFilterValues,
    currentFilterPage,
    setCurrentFilterPage,
    currentItemOffset,
    setCurrentItemOffset
  } = useContext(ResourcesContext);
  const getScopes = () => {
    if(buyNow) {
      return ['list_priced', 'show_on_web', 'ordered_by_year'];
    }else if(watching) {
      return ['watching'];
    }else if(saleId) {
      return ['show_on_web', 'order_by_lot_number'];
    }else {
      return ['show_on_web', 'ordered_by_year'];
    }
  }
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageFromUrl = () => {
    const raw = parseInt(searchParams.get('page'), 10);
    return Number.isInteger(raw) && raw > 0 ? raw - 1 : null;
  };
  const urlPage = pageFromUrl();
  const [itemOffset, setItemOffset] = useState(
    urlPage !== null
      ? urlPage * PAGE_SIZE
      : (currentItemOffset !== -1 ? currentItemOffset : 0)
  );
  const [currentPage, setCurrentPage] = useState(
    urlPage !== null
      ? urlPage
      : (currentFilterPage !== -1 ? currentFilterPage : 0)
  );
  const [filterChange, setFilterChange] = useState(false);
  const [queryVariables, setQueryVariables] = useState(
    inventoryFilterValues !== {} ?
      inventoryFilterValues :
      {
        saleId: saleId,
        filterColumns: [],
        filterNames: [],
        scopes: getScopes(),
      }
  );

  if(!queryVariables['saleId'] && saleId) {
    queryVariables['saleId'] = saleId;
  }

  useEffect(() => {
    setQueryVariables({
      ...queryVariables,
      scopes: getScopes(),
    })
  }, [buyNow]);

  useEffect(() => {
    if(Object.keys(inventoryFilterValues).length > 0) {
      setQueryVariables(inventoryFilterValues);
    }
    if(currentFilterPage !== -1) {
      setCurrentPage(currentFilterPage);
    }
    if(currentItemOffset !== -1) {
      setItemOffset(currentItemOffset);
    }

  }, [location.pathname])

  const paginated = !homeIndex;
  const { data, previousData } = useQuery(ASSETS_WITH_COUNT,
    {variables: {
      ...queryVariables,
      ...(paginated
        ? {limit: PAGE_SIZE, offset: itemOffset}
        : {limit: HOME_PREVIEW_SIZE, offset: 0}),
    }}
  );

  const { data: navData } = useQuery(ASSET_NAV_LIST,
    {variables: queryVariables, skip: homeIndex}
  );

  useEffect(() => {
    if(navData) {
      setCurrentAssets(navData.assetsWithCount.entries);
    }
  }, [navData])

  const currentItems = data?.assetsWithCount.entries || [];
  const stableData = data || previousData;
  const totalCount = stableData?.assetsWithCount.count || 0;
  const pageCount = paginated
    ? Math.ceil(totalCount / PAGE_SIZE)
    : Math.ceil(currentItems.length / PAGE_SIZE);

  const handlePageClick = (event) => {
    const newOffset = event.selected * PAGE_SIZE;
    setCurrentPage(event.selected);
    setItemOffset(newOffset);
    setCurrentItemOffset(newOffset);
    setCurrentFilterPage(event.selected);
    setSearchParams(event.selected > 0 ? {page: String(event.selected + 1)} : {});
  };

  useEffect(() => {
    if(homeIndex) return;
    const nextPage = pageFromUrl() ?? 0;
    if(nextPage !== currentPage) {
      setCurrentPage(nextPage);
      setItemOffset(nextPage * PAGE_SIZE);
    }
  }, [searchParams])

  useEffect(() => {
    if(filterChange) {
      setFilterChange(false);
      setItemOffset(0);
      setCurrentPage(0);
      if(!homeIndex) setSearchParams({}, {replace: true});
    }
  }, [queryVariables])

  const headerViewMore = (
    <SmallButtonContainer
      to='/inventory'
      style={{cursor: 'pointer', textDecoration: 'none'}}
    >
      View More
    </SmallButtonContainer>
  );

  const viewMore = (
    <div
      style={{
        display: 'flex', justifyContent: 'center',
        marginTop: '15px', marginBottom: '30px'
      }}
    >
      <ButtonContainer
        to='/inventory'
        style={{cursor: 'pointer', textDecoration: 'none'}}
      >
        View More
      </ButtonContainer>
    </div>
  );

  return (
    <div style={{marginTop: homeIndex ? '-50px' : ''}}>
      {!saleId &&
        <InventoryHeader homeIndex={homeIndex} />
      }
      {!homeIndex &&
        <>
          <FinanceLink containerMargin='10px' />
          <div
            style={{marginLeft: '5%', marginTop: '30px', marginBottom: '10px'}}
          >
            Inventory Search
          </div>
        </>
      }
      <div style={{display: 'flex', flex: 1, justifyContent: 'center'}}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
            backgroundColor: '#fff',
            padding: '10px',
            marginLeft: '5%',
            marginRight: '5%',
            marginTop: '12px',
            paddingTop: '25px',
            borderRadius: '5px',
            boxShadow: '-1px 0px 15px -5px dimgray',
          }}
        >
          <Filter
            filterType={'Asset'}
            queryVariables={queryVariables}
            setQueryVariables={setQueryVariables}
            setFilterChange={setFilterChange}
            setInventoryFilterValues={setInventoryFilterValues}
            matchCount={data ? totalCount : null}
            shownCount={data ? currentItems.length : null}
            headerAction={homeIndex ? headerViewMore : null}
          />
        </div>
      </div>
      {data ? (
        <CardsContainer>
          {currentItems.map(asset => {
            return (
              <div key={asset.id} style={{display: 'flex', flexDirection: 'column'}}>
                <Card asset={asset} buyNow={buyNow} saleId={saleId} />
              </div>
            )
          })}
        </CardsContainer>
      ) : (
        <Loading />
      )}
      {homeIndex && viewMore}
      {!homeIndex &&
        <div style={{display: 'flex', justifyContent: 'center'}}>
          <StyledReactPaginate
            breakLabel="..."
            nextLabel=""
            forcePage={currentPage}
            onPageChange={handlePageClick}
            pageRangeDisplayed={4}
            pageCount={pageCount}
            previousLabel=""
            renderOnZeroPageCount={null}
            className="pagination-links"
            containerClassName = {"page-links-container"}
            activeLinkClassName='active-page'
          />
        </div>
      }
    </div>
  );
};

export default Inventory;
